import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { KycAndNotesBanner } from '@/components/app/KycAndNotesBanner';
import { KpiCard } from '@/components/app/KpiCard';
import { Card } from '@/components/ui/card';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate, periodLabel } from '@/lib/format';
import { Users, AlertTriangle, Wrench, Calendar, ListChecks, Bell, Building2 } from 'lucide-react';

export default function ConsultantDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/dashboard/consultant').then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const k = data.kpi;
  return (
    <div>
      <PageHeader title="Consultant dashboard" subtitle="Today's operational priorities" />
      <KycAndNotesBanner pendingKyc={data?.kpi?.pending_kyc_tenants || 0} recentNotes={data?.recent_agent_notes || []} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        <KpiCard label="Today's follow-ups" value={k.today_followup_count} icon={ListChecks} accent testId="kpi-today-followups" />
        <KpiCard label="Open tickets" value={k.open_tickets} icon={Wrench} testId="kpi-open-tickets" />
        <KpiCard label="Overdue" value={formatINR(k.overdue_amount)} icon={AlertTriangle} testId="kpi-overdue" />
        <KpiCard label="Renewals (30 d)" value={k.renewals_30_days} icon={Calendar} testId="kpi-renewals" />
        <KpiCard label="Active leases" value={k.active_leases} icon={Users} testId="kpi-leases" />
        <KpiCard label="Properties" value={k.total_properties} icon={Building2} testId="kpi-properties" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-7 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Today's follow-ups</div>
            <span className="ml-auto text-xs text-muted-foreground">{data.today_followup?.length || 0} items</span>
          </div>
          <div className="divide-y">
            {data.today_followup?.slice(0, 8).map((d) => (
              <div key={d.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <div className="font-medium">{d.tenant_name} <span className="text-muted-foreground">· {d.unit_number}</span></div>
                  <div className="text-xs text-muted-foreground">{periodLabel(d.period)} · due {fmtDate(d.due_date)}</div>
                </div>
                <div className="text-right">
                  <div className="tabular-nums font-medium">{formatINR(d.outstanding)}</div>
                  <StatusPill status={d.status} />
                </div>
              </div>
            ))}
            {!data.today_followup?.length && <div className="py-6 text-center text-sm text-muted-foreground">All caught up.</div>}
          </div>
        </Card>

        <Card className="lg:col-span-5 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Ageing buckets</div>
          </div>
          <div className="space-y-3">
            {data.ageing_buckets?.map((b) => (
              <div key={b.bucket}>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{b.bucket} days</span>
                  <span className="tabular-nums">{b.count} · {formatINR(b.amount)}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-[hsl(var(--role-accent))]" style={{ width: `${Math.min(100, (b.amount / Math.max(1, k.overdue_amount)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-6 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Open maintenance tickets</div>
          </div>
          <div className="divide-y">
            {data.open_tickets?.slice(0, 6).map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">{t.ticket_number} · {t.tenant_name || ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-mono">{t.priority}</span>
                  <StatusPill status={t.status} />
                </div>
              </div>
            ))}
            {!data.open_tickets?.length && <div className="py-6 text-center text-sm text-muted-foreground">No open tickets.</div>}
          </div>
        </Card>

        <Card className="lg:col-span-6 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Renewals due</div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {['30', '60', '90'].map((k2) => (
              <div key={k2} className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">In {k2} days</div>
                <div className="text-2xl font-display font-semibold">{(data.renewals?.[k2] || []).length}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
